/**
 * Currency formatting utilities.
 *
 * Shared across the app for consistent currency display.
 */

export interface FormatCurrencyOptions {
  /**
   * 'auto' (default): drop decimals when the amount is whole, otherwise use
   * Intl's per-currency fraction-digit rules (e.g. JPY → 0, USD → 2, BHD → 3).
   * 0: always round to a whole number, regardless of the amount's fraction.
   */
  fractionDigits?: 'auto' | 0;
}

// Constructing an Intl.NumberFormat is expensive; cache instances so hot
// formatting paths (e.g. list totals rows) don't rebuild one per call.
const numberFormatterCache = new Map<string, Intl.NumberFormat>();

function getCachedNumberFormatter(
  key: string,
  factory: () => Intl.NumberFormat
): Intl.NumberFormat {
  let formatter = numberFormatterCache.get(key);
  if (!formatter) {
    formatter = factory();
    numberFormatterCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Get a cached currency Intl.NumberFormat, keyed on the *effective*
 * fraction-digit mode ('0' or 'auto') rather than the raw `fractionDigits`
 * option — `formatCurrency` also forces 0-decimal formatting for whole
 * amounts, so keying on the option alone would let a whole-amount call
 * populate the cache under the same key a later fractional-amount call
 * looks up under, returning a stale 0-decimal formatter.
 *
 * Can throw for an invalid currency code — callers must handle that.
 */
export function getCachedCurrencyFormatter(
  currency: string,
  fractionDigits: 'auto' | 0,
  hasDecimals: boolean
): Intl.NumberFormat {
  const mode = fractionDigits === 0 || !hasDecimals ? '0' : 'auto';
  const key = `currency:${currency}:${mode}`;
  return getCachedNumberFormatter(key, () => {
    const formatOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency,
    };
    if (mode === '0') {
      formatOptions.minimumFractionDigits = 0;
      formatOptions.maximumFractionDigits = 0;
    }
    return new Intl.NumberFormat('en-US', formatOptions);
  });
}

/** Get a cached plain-decimal Intl.NumberFormat, keyed on its fraction-digit bounds. */
export function getCachedDecimalFormatter(
  minimumFractionDigits: number,
  maximumFractionDigits: number
): Intl.NumberFormat {
  const key = `decimal:${minimumFractionDigits},${maximumFractionDigits}`;
  return getCachedNumberFormatter(
    key,
    () =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
      })
  );
}

/**
 * Format a number as currency, dropping decimals when the amount is whole.
 *
 * Uses Intl's built-in fraction-digit rules per currency (e.g. JPY → 0, USD → 2, BHD → 3)
 * instead of hardcoding 2 decimal places. Formatter instances are cached per
 * effective (currency, fraction-mode) key — see `getCachedCurrencyFormatter`.
 *
 * @example
 * formatCurrency(4500, 'USD')    // → "$4,500"
 * formatCurrency(4500.5, 'USD')  // → "$4,500.50"
 * formatCurrency(0, 'EUR')       // → "€0"
 * formatCurrency(1000, 'JPY')    // → "¥1,000"
 * formatCurrency(4500.5, 'USD', { fractionDigits: 0 }) // → "$4,501"
 */
export function formatCurrency(
  amount: number,
  currency: string,
  options: FormatCurrencyOptions = {}
): string {
  const { fractionDigits = 'auto' } = options;
  const hasDecimals = amount % 1 !== 0;

  try {
    return getCachedCurrencyFormatter(
      currency,
      fractionDigits,
      hasDecimals
    ).format(amount);
  } catch {
    return `${amount}`;
  }
}
